import dayjs from 'dayjs';

/**
 * MR price prefill (2026-09-29): at submit the server fills coded lines with the price we last
 * paid (a PO) or, for items never bought, the catalog list price. These fields come back on MR
 * lines for procurement, approvers and admins only; requesters never receive them.
 */
export interface PrefillInfo {
  prefillPrice?: number | null;
  prefillSource?: 'po' | 'list' | null;
  prefillRef?: string | null;
  prefillVendorName?: string | null;
  prefillVendorId?: number | null;
  prefillDate?: string | null;
  requesterEstPrice?: number | null;
  catalogPrice?: number | null;
  priceHistory?: {
    count: number;
    min: number;
    max: number;
    /** Middle price of those purchases ("usually ~"), 2026-10-18. Missing on servers older than that. */
    median?: number;
    latest: { price: number; poId: string; date: string | null; vendorName: string | null };
  } | null;
}

const money = (n: number) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });

/** True while the line still carries the price the submit step filled in. */
export function onPrefill(l: PrefillInfo & { unitPrice?: number | string | null }): boolean {
  if (!l.prefillSource || l.prefillPrice == null || l.unitPrice == null) return false;
  return Math.abs(Number(l.unitPrice) - Number(l.prefillPrice)) < 0.005;
}

/** "last paid · PO-000544 · Salem · 31 Aug" or "list price · Salem · 6 Sep". */
export function prefillLabel(l: PrefillInfo): string {
  const parts = [
    l.prefillSource === 'po' ? 'last paid' : 'list price',
    l.prefillSource === 'po' ? l.prefillRef : null,
    l.prefillVendorName,
    l.prefillDate ? dayjs(l.prefillDate).format('D MMM') : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

/**
 * The same label split for table cells, whose source line never wraps:
 * short = "last paid · PO-000544" / "list price"; where = "Salem · 31 Aug" (goes on its own, wrapping line).
 */
export function prefillLabelParts(l: PrefillInfo): { short: string; where: string } {
  const short = l.prefillSource === 'po' ? ['last paid', l.prefillRef].filter(Boolean).join(' · ') : 'list price';
  const where = [l.prefillVendorName, l.prefillDate ? dayjs(l.prefillDate).format('D MMM') : null].filter(Boolean).join(' · ');
  return { short, where };
}

/**
 * "bought once, 340" / "bought 3 times, always 1,440" / "bought 19 times, 17 – 2,750 · usually ~120";
 * null when never bought in this unit.
 */
export function historyLabel(l: PrefillInfo): string | null {
  const h = l.priceHistory;
  if (!h || h.count < 1) return null;
  if (h.count === 1) return `bought once, ${money(h.min)}`;
  if (h.max - h.min < 0.005) return `bought ${h.count} times, always ${money(h.min)}`;
  const usually = h.count >= 3 && h.median != null ? ` · usually ~${money(h.median)}` : '';
  return `bought ${h.count} times, ${money(h.min)} – ${money(h.max)}${usually}`;
}

/** True when the line still carries the price the R&D / Marketing requester typed (2026-10-18). */
export function onRequesterPrice(l: PrefillInfo & { unitPrice?: number | string | null }): boolean {
  if (l.requesterEstPrice == null || l.unitPrice == null) return false;
  return Number(l.requesterEstPrice) > 0 && Math.abs(Number(l.unitPrice) - Number(l.requesterEstPrice)) < 0.005;
}

/** Ratio at which a requester's price is flagged against the usual price (Ahmed, D1-a, 2026-10-18). */
export const PRICE_CHECK_RATIO = 2;

/**
 * Price check for a line still on the requester's price: compares it with the usual (middle) price we paid
 * for the item in the same unit. 'warn' when one is at least 2x the other, either way; never changes the price.
 */
export function priceCheck(l: PrefillInfo & { unitPrice?: number | string | null }): { level: 'ok' | 'warn'; text: string } | null {
  const h = l.priceHistory;
  if (!onRequesterPrice(l) || !h || h.median == null || h.median <= 0) return null;
  const price = Number(l.unitPrice);
  const usual = h.median;
  const ratio = Math.max(price, usual) / Math.min(price, usual);
  const r = ratio.toLocaleString('en-US', { maximumFractionDigits: 1 });
  if (ratio >= PRICE_CHECK_RATIO) {
    if (usual > price) return { level: 'warn', text: `we usually pay ${money(usual)}, ${r}× this price` };
    return { level: 'warn', text: ratio < 2.05 ? `we usually pay ${money(usual)}, half this price` : `we usually pay ${money(usual)}, this price is ${r}× that` };
  }
  return { level: 'ok', text: ratio < 1.1 ? 'matches what we usually pay' : `within 2× of the usual price (~${money(usual)})` };
}

/** The usual (middle) price we paid for the line's item in its unit, or null without purchase history. */
export function usualPrice(l: PrefillInfo): number | null {
  const m = l.priceHistory?.median;
  return m != null && m > 0 ? m : null;
}

/** "usually 2,601 · bought 3 times · last paid 2,601 · PO-000561" — the procurement aid on Create PO (2026-10-19). */
export function historyHint(l: PrefillInfo): string | null {
  const h = l.priceHistory;
  const usual = usualPrice(l);
  if (!h || usual == null) return null;
  const times = h.count === 1 ? 'bought once' : h.count === 2 ? 'bought twice' : `bought ${h.count} times`;
  return [`usually ${money(usual)}`, times, `last paid ${money(h.latest.price)}`, h.latest.poId].filter(Boolean).join(' · ');
}

export interface EstimateLine extends PrefillInfo {
  lineNo?: number;
  qty?: number | string | null;
  lineStatus?: string | null;
  lineApprovalStatus?: string | null;
}

/**
 * History estimate for approvers (Ahmed, 2026-10-19): quantity x usual price over the lines that have purchase
 * history in their unit. Never written into a price. Cancelled and rejected lines are left out, like the server.
 */
export function historyEstimate<T extends EstimateLine>(lines: T[]): { total: number; covered: T[]; uncovered: T[]; counted: number } {
  const live = lines.filter((l) => l.lineStatus !== 'Cancelled' && l.lineApprovalStatus !== 'Rejected');
  const covered = live.filter((l) => usualPrice(l) != null);
  const uncovered = live.filter((l) => usualPrice(l) == null);
  const total = covered.reduce((s, l) => s + Number(l.qty || 0) * (usualPrice(l) as number), 0);
  return { total: Math.round(total * 100) / 100, covered, uncovered, counted: live.length };
}

/**
 * Tag text when the history estimate and the requested total are 2x or more apart (either way), else null.
 * "estimate 8.8× requested" when the estimate is higher; "requested 8.4× the estimate" when it is lower (2026-10-19).
 */
export function estimateGapLabel(estimate: number, requested: number): string | null {
  if (!(estimate > 0) || !(requested > 0)) return null;
  const ratio = Math.max(estimate, requested) / Math.min(estimate, requested);
  if (ratio < PRICE_CHECK_RATIO) return null;
  const r = ratio.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return estimate > requested ? `estimate ${r}× requested` : `requested ${r}× the estimate`;
}

/** "last paid 2,750 · PO-000735 · Loay - Pluming · 14 Oct" for a line whose price did not come from history. */
export function lastPaidNote(l: PrefillInfo): string | null {
  if (l.prefillPrice == null || !l.prefillSource) return null;
  const parts = [
    `${l.prefillSource === 'po' ? 'last paid' : 'list price'} ${money(Number(l.prefillPrice))}`,
    l.prefillSource === 'po' ? l.prefillRef : null,
    l.prefillVendorName,
    l.prefillDate ? dayjs(l.prefillDate).format('D MMM') : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

/** A purchase made after the price was filled in, while the line is not ordered yet. */
export function newerPurchase(l: PrefillInfo, ordered: boolean) {
  const h = l.priceHistory;
  if (ordered || l.prefillSource !== 'po' || !h || h.latest.poId === l.prefillRef) return null;
  if (l.prefillDate && h.latest.date && dayjs(h.latest.date).isBefore(dayjs(l.prefillDate), 'day')) return null;
  return h.latest;
}

export { money as fmtPrice };
